import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import {API_URL} from '../../constants';
import {AuthContext} from '../AuthContext';
import Spinner from 'react-bootstrap/Spinner';
import {AXIOS_NOT_FOUND_REDIRECT_OPT_OUT} from '../../axiosNotFoundRedirect';

export const DRIVER_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;
export const DRIVER_DOCUMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];
const ALLOWED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const EMPTY_DOCUMENTS = {};
const MIME_TYPE_BY_EXTENSION = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
};

const DOCUMENTS = [
    {
        field: 'driver_front_license',
        label: 'Front License',
        uploadType: 'front',
    },
    {
        field: 'driver_back_license',
        label: 'Back License',
        uploadType: 'back',
    },
    {
        field: 'driver_proof_of_work',
        label: 'Proof Of Work',
        uploadType: 'proof',
    },
    {
        field: 'driver_insurance',
        label: 'Insurance',
        uploadType: 'insurance',
    },
];

const UPLOAD_LABEL_STYLE = {
    bottom: '-2px',
    right: '8px',
    background: '#007bff',
    cursor: 'pointer',
    borderRadius: '50%',
    width: '34px',
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
};

export const validateDriverDocument = (file) => {
    if (!file) return 'Select a document to upload.';

    const extension = file.name?.split('.').pop()?.toLowerCase();
    const mimeType = file.type?.toLowerCase();
    const hasAllowedExtension = ALLOWED_EXTENSIONS.includes(extension);
    const hasAllowedMimeType = !mimeType
        || (ALLOWED_MIME_TYPES.includes(mimeType) && MIME_TYPE_BY_EXTENSION[extension] === mimeType);

    if (!hasAllowedExtension || !hasAllowedMimeType) {
        return 'Documents must be PDF, JPEG, or PNG files.';
    }

    if (file.size > DRIVER_DOCUMENT_MAX_BYTES) {
        return 'Documents must be 10 MB or smaller.';
    }

    return null;
};

const normalizeMimeType = (response) => {
    const blobType = response?.data instanceof Blob ? response.data.type : '';
    const headerType = response?.headers?.['content-type'] || response?.headers?.get?.('content-type') || '';

    return String(headerType || blobType)
        .split(';')[0]
        .trim()
        .toLowerCase();
};

const DriverDocuments = ({
    canUpdate = false,
    children,
    driverId,
    documents = EMPTY_DOCUMENTS,
    uploadingFile = null,
    onUpload,
}) => {
    const {token, setToken} = useContext(AuthContext);
    const navigate = useNavigate();
    const currentDriverId = String(driverId);
    const [expandedDriverId, setExpandedDriverId] = useState(null);
    const [previews, setPreviews] = useState({});
    const [loading, setLoading] = useState({});
    const [loadErrors, setLoadErrors] = useState({});
    const [validationError, setValidationError] = useState('');
    const objectUrlsRef = useRef({});
    const requestIdRef = useRef(0);
    const showDocuments = expandedDriverId === currentDriverId;

    const documentEntries = useMemo(() => DOCUMENTS.map((definition) => {
        const {field} = definition;
        const metadata = documents?.[field];

        return {
            ...definition,
            available: metadata?.available === true,
            downloadUrl: metadata?.download_url,
        };
    }), [documents]);

    const revokeObjectUrls = useCallback(() => {
        Object.values(objectUrlsRef.current).forEach((url) => {
            window.URL.revokeObjectURL(url);
        });
        objectUrlsRef.current = {};
    }, []);

    const clearLoadedDocuments = useCallback(() => {
        requestIdRef.current += 1;
        revokeObjectUrls();
        setPreviews({});
        setLoading({});
        setLoadErrors({});
    }, [revokeObjectUrls]);

    useEffect(() => {
        setExpandedDriverId(null);
        setValidationError('');
        setPreviews({});
        setLoading({});
        setLoadErrors({});

        return () => {
            requestIdRef.current += 1;
            revokeObjectUrls();
        };
    }, [currentDriverId, revokeObjectUrls]);

    useEffect(() => {
        if (!showDocuments || !token) return undefined;

        const availableDocuments = documentEntries.filter(({available}) => available);
        const requestId = ++requestIdRef.current;
        const abortController = new AbortController();
        revokeObjectUrls();
        setPreviews({});
        setLoadErrors({});
        setLoading(Object.fromEntries(availableDocuments.map(({field}) => [field, true])));

        availableDocuments.forEach(async ({downloadUrl, field, label}) => {
            const expectedDownloadUrl = `/api/admin/drivers/${encodeURIComponent(currentDriverId)}/documents/${field}`;

            if (downloadUrl !== expectedDownloadUrl) {
                if (requestIdRef.current === requestId) {
                    setLoading((current) => ({...current, [field]: false}));
                    setLoadErrors((current) => ({
                        ...current,
                        [field]: `Unable to load ${label} securely.`,
                    }));
                }
                return;
            }

            try {
                const response = await axios.get(`${API_URL}${downloadUrl}`, {
                    ...AXIOS_NOT_FOUND_REDIRECT_OPT_OUT,
                    headers: {Authorization: `Bearer ${token}`},
                    responseType: 'blob',
                    signal: abortController.signal,
                });

                if (requestIdRef.current !== requestId) return;

                const mimeType = normalizeMimeType(response);
                if (!ALLOWED_MIME_TYPES.includes(mimeType)) {
                    setLoadErrors((current) => ({
                        ...current,
                        [field]: `Unable to preview ${label}: unsupported file type.`,
                    }));
                    return;
                }

                const responseBlob = response.data instanceof Blob
                    ? response.data
                    : new Blob([response.data], {type: mimeType});
                const previewBlob = responseBlob.type === mimeType
                    ? responseBlob
                    : new Blob([responseBlob], {type: mimeType});
                const objectUrl = window.URL.createObjectURL(previewBlob);

                if (requestIdRef.current !== requestId) {
                    window.URL.revokeObjectURL(objectUrl);
                    return;
                }

                objectUrlsRef.current[field] = objectUrl;
                setPreviews((current) => ({
                    ...current,
                    [field]: {mimeType, url: objectUrl},
                }));
            } catch (error) {
                if (requestIdRef.current !== requestId) return;

                if (error?.response?.status === 401) {
                    abortController.abort();
                    requestIdRef.current += 1;
                    revokeObjectUrls();
                    setPreviews({});
                    setLoading({});
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                    return;
                }

                setLoadErrors((current) => ({
                    ...current,
                    [field]: `Unable to load ${label} securely.`,
                }));
            } finally {
                if (requestIdRef.current === requestId) {
                    setLoading((current) => ({...current, [field]: false}));
                }
            }
        });

        return () => {
            abortController.abort();
            requestIdRef.current += 1;
            revokeObjectUrls();
        };
    }, [currentDriverId, documentEntries, navigate, revokeObjectUrls, setToken, showDocuments, token]);

    const toggleDocuments = () => {
        if (showDocuments) {
            clearLoadedDocuments();
            setValidationError('');
            setExpandedDriverId(null);
            return;
        }

        setExpandedDriverId(currentDriverId);
    };

    const selectUpload = (event, uploadType) => {
        if (!canUpdate) return;

        const file = event.target.files?.[0];
        event.target.value = '';
        const error = validateDriverDocument(file);

        if (error) {
            setValidationError(error);
            return;
        }

        setValidationError('');
        onUpload?.(file, uploadType);
    };

    return (
        <div className="card mb-3" data-testid="driver-documents">
            <div className="card-body">
                <h5 className="text-center mb-0">
                    <button
                        type="button"
                        className="btn btn-link p-0"
                        style={{fontSize: 'inherit', fontWeight: 'inherit', textDecoration: 'none'}}
                        aria-expanded={showDocuments}
                        aria-controls={`driver-documents-content-${driverId}`}
                        onClick={toggleDocuments}
                    >
                        Documents {showDocuments ? '▼' : '▶'}
                    </button>
                </h5>

                <div
                    id={`driver-documents-content-${driverId}`}
                    data-testid="driver-documents-content"
                    className={`fade-section driver-documents-section ${showDocuments ? 'show' : 'hide'}`}
                    hidden={!showDocuments}
                >
                    {validationError && (
                        <div className="alert alert-danger mt-2" role="alert">{validationError}</div>
                    )}

                    <div className="row mt-2">
                        {documentEntries.map(({available, field, label, uploadType}) => {
                            const preview = previews[field];
                            const loadError = loadErrors[field];

                            return (
                                <div className="col-md-6 mb-2" key={field}>
                                    <div className="card p-2 h-100 position-relative text-center">
                                        <div>{label}</div>
                                        {canUpdate && (
                                            <label
                                                className="position-absolute"
                                                style={UPLOAD_LABEL_STYLE}
                                                aria-label={`Replace ${label}`}
                                            >
                                                {uploadingFile === uploadType ? (
                                                    <Spinner animation="border" size="sm"/>
                                                ) : (
                                                    <i className="fa fa-camera" aria-hidden="true"/>
                                                )}
                                                <input
                                                    type="file"
                                                    accept={DRIVER_DOCUMENT_ACCEPT}
                                                    aria-label={`Upload ${label}`}
                                                    disabled={uploadingFile !== null}
                                                    onChange={(event) => selectUpload(event, uploadType)}
                                                    className="btn form-control w-50 d-none"
                                                />
                                            </label>
                                        )}

                                        <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1">
                                            {loading[field] ? (
                                                <div className="py-4" role="status" aria-label={`Loading ${label}`}>
                                                    <Spinner animation="border" size="sm"/>
                                                </div>
                                            ) : preview ? (
                                                <>
                                                    {preview.mimeType === 'application/pdf' ? (
                                                        <iframe
                                                            title={`${label} preview`}
                                                            src={preview.url}
                                                            style={{width: '100%', minHeight: '300px', border: 0}}
                                                        />
                                                    ) : (
                                                        <img
                                                            src={preview.url}
                                                            alt={`${label} preview`}
                                                            style={{maxWidth: '100%', height: '300px', objectFit: 'contain'}}
                                                        />
                                                    )}
                                                    <a
                                                        href={preview.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-link btn-sm"
                                                    >
                                                        Open {label}
                                                    </a>
                                                </>
                                            ) : loadError ? (
                                                <div className="alert alert-danger my-2" role="alert">{loadError}</div>
                                            ) : available ? (
                                                <span className="text-muted">Preview unavailable</span>
                                            ) : (
                                                'Not Uploaded'
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {typeof children === 'function'
                            ? children({expanded: showDocuments})
                            : children}
                    </div>
                </div>
            </div>

            <style>{`
                .fade-section.driver-documents-section {
                    max-height: 0;
                    opacity: 0;
                    overflow: hidden;
                    transition: max-height 0.4s ease-in-out, opacity 0.4s ease-in-out;
                }

                .fade-section.driver-documents-section.show {
                    max-height: 6000px;
                    opacity: 1;
                }

                .fade-section.driver-documents-section.hide {
                    max-height: 0;
                    opacity: 0;
                }
            `}</style>
        </div>
    );
};

export default DriverDocuments;
